import React, { useEffect, useMemo, useState, useRef, KeyboardEventHandler } from 'react';
import { FuelDatePicker, FuelTextField } from '@frete.com/fuel-react';
import { InputProps } from './types';
import { useMask } from '@/hooks';
import { isEqual } from '@/utils';
import { getNoOnlyWhiteSpaces } from '@/utils/string';

export const Input: React.FC<InputProps> = ({ ...props }) => {
  const {
    value = '',
    onUpdate,
    label,
    placeholder,
    maxLength,
    error,
    name,
    onEnter,
    onFocus,
    onBlur,
    autoFocus,
    required = false,
    disabled = false,
    type = 'text',
    mask = [],
    unmaskedValue = false,
    monetary = false,
    minDate,
    maxDate,
  } = props;

  const [maskedValue, setMaskedValue] = useState(value);
  const masked = useMask(mask, monetary);
  const hasMask = useMemo(() => !!mask?.length || !!monetary, [mask, monetary]);
  const displayValue = useMemo(() => (hasMask ? maskedValue : value), [hasMask, maskedValue, value]);
  const datePickerRef = useRef<HTMLFuelDatePickerElement>();
  const [datePickerStatus, setDatePickerStatus] = useState(false);
  const localInputReference = useRef<HTMLFuelTextFieldElement>(null);

  const handleUpdate = (valueToUpdate: string | number) => {
    if (isEqual(valueToUpdate.toString(), value.toString())) return;
    onUpdate(valueToUpdate);
  };

  const onBeforeUpdate = (val: string) => {
    const localValue = getNoOnlyWhiteSpaces(val);

    if (!hasMask) {
      if (localInputReference.current) localInputReference.current.value = localValue;
      handleUpdate(localValue);
      return;
    }

    const maskedResult = masked(localValue);
    setMaskedValue(maskedResult.value);
    handleUpdate(unmaskedValue ? maskedResult.unmaskedValue : maskedResult.value);
  };

  const onValueChange = () => {
    onBeforeUpdate(value.toString());
  };

  const initState = () => {
    if (!value || !value.toString().length) return;
    onValueChange();
  };

  const onInput = (event: CustomEvent) => onBeforeUpdate(event.detail.value);

  const currentError = useMemo(() => (!disabled && typeof error === 'string' ? error : ''), [error, disabled]);

  const currentState = useMemo(() => {
    if (type === 'text') return currentError ? 'error' : 'default';
    return Boolean(datePickerStatus) || currentError ? 'error' : 'default';
  }, [datePickerStatus, currentError, type]);

  const validateDatePickerError = () => {
    const errorHint = datePickerRef.current?.shadowRoot.querySelector('.fuel-date-picker__hint');
    setDatePickerStatus(Boolean(errorHint));
  };

  const onKeyUp: KeyboardEventHandler<HTMLFuelTextFieldElement> = ({ key }) => {
    if (key === 'Enter' && onEnter) onEnter();
  };

  const reference = (element: HTMLFuelTextFieldElement) => {
    localInputReference.current = element;
  };

  useEffect(initState, []);
  useEffect(onValueChange, [value]);
  useEffect(validateDatePickerError, [error]);

  return (
    <>
      {type === 'text' && (
        <FuelTextField
          controlled
          disabled={disabled}
          hint={currentError}
          label={label}
          maxlength={maxLength}
          name={name}
          onFuel-input={onInput}
          onFuel-focus={onFocus}
          onFuel-blur={onBlur}
          placeholder={placeholder}
          setAutofocus={autoFocus}
          required={required}
          state={currentState}
          value={displayValue as string}
          onKeyUp={onKeyUp}
          ref={reference}
        />
      )}
      {type === 'date' && (
        <FuelDatePicker
          disabled={disabled}
          hint={currentError}
          label={label}
          max={maxDate}
          min={minDate}
          name={name}
          onBlur={validateDatePickerError}
          onFuel-change={onInput}
          ref={datePickerRef}
          state={currentState}
          value={displayValue as string}
        />
      )}
    </>
  );
};
